import React, { useEffect } from 'react';

import { Select } from '@99designs/common/components/Select';
import { SquarespaceIndustry, SquarespaceOrganizationSize } from '__generated__/types';
import Text from 'common/components/Text';
import { handlePromise } from 'common/utils/Bugsnag';
import { ObservableField, ObservableForm } from 'projects/components/ObservableForm';
import { trackViewedBriefEvent } from 'projects/events';
import ContentSetting from 'projects/components/ContentSetting';

interface Fields {
    organizationSize: SquarespaceOrganizationSize;
    industry: SquarespaceIndustry;
    organizationRole: string;
}

interface Props<T> {
    form: ObservableForm<T>;
    brief: { id: string };
}

const AboutYouPage: <T extends Fields>(props: Props<T>) => React.ReactElement<Props<T>> = ({
    form,
    brief,
}) => {
    useEffect(() => {
        trackViewedBriefEvent({
            id: brief.id,
            page: 'AboutYou',
        });
    }, []);

    return (
        <ContentSetting>
            <ObservableField
                form={form}
                name="organizationSize"
                title={__('What is the size of your organization?')}
                render={() => (
                    <Select
                        name="organizationSize"
                        options={[
                            {
                                value: '',
                                disabled: true,
                                label: __('Select size'),
                            },
                            { value: SquarespaceOrganizationSize.ONE, label: __('Just me') },
                            { value: SquarespaceOrganizationSize.TWO_TO_FOUR, label: __('2-4') },
                            { value: SquarespaceOrganizationSize.FIVE_TO_NINE, label: __('5-9') },
                            {
                                value: SquarespaceOrganizationSize.TEN_OR_MORE,
                                label: __('10 or more'),
                            },
                        ]}
                        value={form.get('organizationSize')}
                        onChange={({ value }) => {
                            handlePromise(
                                form.set('organizationSize', value as SquarespaceOrganizationSize),
                                'AboutYouPage'
                            );
                        }}
                        variations={['max']}
                    />
                )}
            />

            <ObservableField
                form={form}
                name="industry"
                title={__('What is your industry?')}
                render={() => (
                    <Select
                        name="industry"
                        options={[
                            {
                                value: '',
                                disabled: true,
                                label: __('Select industry'),
                            },
                            {
                                value: SquarespaceIndustry.ACCOUNTING_AND_FINANCIAL,
                                label: __('Accounting & financial'),
                            },
                            {
                                value: SquarespaceIndustry.ART_AND_DESIGN,
                                label: __('Art & design'),
                            },
                            {
                                value: SquarespaceIndustry.BEAUTY_AND_PERSONAL_CARE,
                                label: __('Beauty & personal care'),
                            },
                            {
                                value: SquarespaceIndustry.COMMUNITY_AND_NONPROFIT,
                                label: __('Community & nonprofit'),
                            },
                            {
                                value: SquarespaceIndustry.ENTERTAINMENT_AND_MEDIA,
                                label: __('Entertainment & media'),
                            },
                            { value: SquarespaceIndustry.FASHION, label: __('Fashion') },
                            {
                                value: SquarespaceIndustry.FOOD_AND_DRINK,
                                label: __('Food & drink'),
                            },
                            {
                                value: SquarespaceIndustry.HEALTH_AND_FITNESS,
                                label: __('Health & fitness'),
                            },
                            {
                                value: SquarespaceIndustry.HOME_AND_LIVING,
                                label: __('Home & living'),
                            },
                            {
                                value: SquarespaceIndustry.INTERIOR_DESIGN,
                                label: __('Interior design'),
                            },
                            {
                                value: SquarespaceIndustry.LEGAL_SERVICES_AND_ATTORNEYS,
                                label: __('Legal services & attorneys'),
                            },
                            {
                                value: SquarespaceIndustry.MARKETING_AND_ADVERTISING,
                                label: __('Marketing & advertising'),
                            },
                            { value: SquarespaceIndustry.MUSIC, label: __('Music') },
                            { value: SquarespaceIndustry.PHOTOGRAPHY, label: __('Photography') },
                            { value: SquarespaceIndustry.REAL_ESTATE, label: __('Real estate') },
                            {
                                value: SquarespaceIndustry.RETAIL_RESTAURANT_AND_BAR,
                                label: __('Retail, restaurant & bar'),
                            },
                            { value: SquarespaceIndustry.TECHNOLOGY, label: __('Technology') },
                            {
                                value: SquarespaceIndustry.TRAVEL_AND_LIFESTYLE,
                                label: __('Travel & lifecycle'),
                            },
                            { value: SquarespaceIndustry.WEDDING, label: __('Wedding') },
                        ]}
                        value={form.get('industry')}
                        onChange={({ value }) => {
                            handlePromise(
                                form.set('industry', value as SquarespaceIndustry),
                                'AboutYouPage'
                            );
                        }}
                        variations={['max']}
                    />
                )}
            />

            <ObservableField
                form={form}
                name="organizationRole"
                title={__('What is your role?')}
                render={() => <Text fill={true} {...form.attach('organizationRole')} />}
            />
        </ContentSetting>
    );
};

export default AboutYouPage;
